import React, { useEffect, useMemo, useRef, useState } from "react";

// --- Product name ---
// ZapPilot — pilot your WhatsApp outreach

// Default contact (São Paulo mobile): +55 11 96800-0822
const DEFAULT_NUMBER = "+55 11 96800-0822";

// LocalStorage keys
const LS_TEMPLATES_KEY = "wa_bot_templates_v1";
const LS_NUMBER_KEY = "wa_bot_number_v1";
const LS_SELECTED_TEMPLATE_ID = "wa_bot_selected_template_id_v1";
const LS_STRIP_UNRESOLVED = "wa_bot_strip_unresolved_v1";
const LS_RECENT_NUMBERS = "wa_bot_recent_numbers_v1";
const LS_DOMAIN_MODE = "wa_bot_domain_mode_v1"; // 'auto' | 'web' | 'api' | 'wame'

interface Template {
  id: string;
  name: string;
  body: string;
}

interface CheckResult {
  id: string;
  label: string;
  ok: boolean;
}

// Some starter templates
const STARTER_TEMPLATES: Template[] = [
  {
    id: cryptoRandomId(),
    name: "Friendly intro (EN)",
    body:
      "Hey {{name}}, this is {{from}}. Quick question: is now a good time to talk?",
  },
  {
    id: cryptoRandomId(),
    name: "Follow-up (PT-BR)",
    body:
      "Oi {{name}}! Aqui é {{from}}. Podemos retomar aquela conversa sobre {{topic}} hoje?",
  },
  {
    id: cryptoRandomId(),
    name: "Link drop",
    body:
      "Hi {{name}} — here's the link we discussed: {{url}}. If you have any questions, tell me!",
  },
];

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const arr = new Uint32Array(2);
    crypto.getRandomValues(arr);
    return (
      arr[0].toString(36) + Math.abs(arr[1]).toString(36)
    ).slice(0, 12);
  }
  return Math.random().toString(36).slice(2, 14);
}

function classNames(...xs: any[]): string {
  return xs.filter(Boolean).join(" ");
}

function useLocalStorage<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const v = localStorage.getItem(key);
      return v != null ? JSON.parse(v) : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
}

function normalizeToWhatsAppDigits(input: string): string {
  // Keep digits only; WhatsApp expects country+number without + or punctuation
  const digits = (input || "").replace(/\D/g, "");
  return digits.startsWith("00") ? digits.slice(2) : digits; // tolerate 00-prefix
}

function extractPlaceholders(templateBody: string): string[] {
  const re = /\{\{\s*([a-zA-Z0-9_\-\.]+)\s*\}\}/g;
  const keys = new Set<string>();
  let m;
  while ((m = re.exec(templateBody))) keys.add(m[1]);
  return Array.from(keys);
}

function fillTemplate(body: string, vars: Record<string, string>): string {
  return body.replace(/\{\{\s*([a-zA-Z0-9_\-\.]+)\s*\}\}/g, (m: string, k: string) => {
    const v = vars[k];
    return v != null && v !== "" ? String(v) : m; // leave token in place if empty
  });
}

function stripUnresolvedTokens(text: string): string {
  return text
    .replace(/\{\{\s*[a-zA-Z0-9_\-.]+\s*\}\}/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+,/g, ",")
    .trim();
}

// Minimal CSV parser: handles quotes, commas, newlines, and escaped quotes.
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let i = 0, cur = '', row: string[] = [], inQuotes = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      cur += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ',') { row.push(cur); cur = ''; i++; continue; }
    if (ch === '\n' || ch === '\r') {
      // handle CRLF/CR/LF
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cur); rows.push(row); cur = ''; row = []; i++; continue;
    }
    cur += ch; i++;
  }
  row.push(cur); rows.push(row);
  // Trim possible trailing empty final row
  return rows.filter(r => !(r.length === 1 && r[0] === ''));
}

interface IconProps {
  className?: string;
}

function CheckIcon({ className = "w-4 h-4" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function Spinner({ className = "w-4 h-4" }: IconProps) {
  return (
    <svg className={className + " animate-spin"} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" fill="none" />
    </svg>
  );
}

function isMobileUA(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent);
}

function getUrlForMode(mode: string, digits: string, text: string): string {
  if (!digits) return "";
  const t = encodeURIComponent(text || "");
  switch (mode) {
    case "web":
      return `https://web.whatsapp.com/send?phone=${digits}&text=${t}`;
    case "api":
      return `https://api.whatsapp.com/send?phone=${digits}&text=${t}&type=phone_number&app_absent=0`;
    case "wame":
      return `https://wa.me/${digits}?text=${t}`;
    case "auto":
    default:
      return isMobileUA()
        ? `https://wa.me/${digits}?text=${t}`
        : `https://web.whatsapp.com/send?phone=${digits}&text=${t}`;
  }
}

export default function WhatsAppTemplateBot() {
  const [number, setNumber] = useLocalStorage(LS_NUMBER_KEY, DEFAULT_NUMBER);
  const [templates, setTemplates] = useLocalStorage<Template[]>(
    LS_TEMPLATES_KEY,
    STARTER_TEMPLATES
  );
  const [selectedId, setSelectedId] = useLocalStorage<string | null>(
    LS_SELECTED_TEMPLATE_ID,
    STARTER_TEMPLATES[0]?.id || null
  );
  const [strip, setStrip] = useLocalStorage(LS_STRIP_UNRESOLVED, false);
  const [recents, setRecents] = useLocalStorage<string[]>(LS_RECENT_NUMBERS, []);
  const [domainMode, setDomainMode] = useLocalStorage(LS_DOMAIN_MODE, "auto");

  // Keep selectedId valid if templates change
  useEffect(() => {
    if (!templates.find((t: Template) => t.id === selectedId)) {
      setSelectedId(templates[0]?.id || null);
    }
  }, [templates, selectedId, setSelectedId]);

  const selected = templates.find((t: Template) => t.id === selectedId) || templates[0];

  const placeholderKeys = useMemo(
    () => extractPlaceholders(selected?.body || ""),
    [selected?.body]
  );

  const [vars, setVars] = useState<Record<string, string>>(() => ({}));
  useEffect(() => {
    // Reset vars when template changes; prefill common keys
    const base = Object.fromEntries(placeholderKeys.map((k) => [k, ""]));
    if ("from" in base && !base.from) base.from = "Brian";
    setVars(base);
  }, [selected?.id, placeholderKeys.join(",")]);

  const compiled = useMemo(() => {
    let out = fillTemplate(selected?.body || "", vars);
    if (strip) out = stripUnresolvedTokens(out);
    return out;
  }, [selected?.body, vars, strip]);

  const waDigits = normalizeToWhatsAppDigits(number);

  const primaryUrl = getUrlForMode(domainMode, waDigits, compiled);
  const apiUrl = getUrlForMode("api", waDigits, compiled); // persistent fallback

  const fileInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  function addRecent(n: string) {
    const val = (n || '').trim();
    if (!val) return;
    setRecents((xs: string[]) => {
      const arr = [val, ...xs.filter((x: string) => x !== val)];
      return arr.slice(0, 5);
    });
  }

  function addTemplate() {
    const t: Template = {
      id: cryptoRandomId(),
      name: "New template",
      body: "Hello {{name}}, this is {{from}}.",
    };
    setTemplates((xs: Template[]) => [t, ...xs]);
    setSelectedId(t.id);
  }

  function duplicateTemplate(id: string) {
    const t = templates.find((x: Template) => x.id === id);
    if (!t) return;
    const copy = { ...t, id: cryptoRandomId(), name: `${t.name} (copy)` };
    setTemplates((xs: Template[]) => [copy, ...xs]);
    setSelectedId(copy.id);
  }

  function deleteTemplate(id: string) {
    const xs = templates.filter((t: Template) => t.id !== id);
    setTemplates(xs);
  }

  function onImportJson(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result));
        if (!Array.isArray(data)) throw new Error("Invalid JSON: expected array");
        const clean = data
          .filter((t) => t && typeof t.name === "string" && typeof t.body === "string")
          .map((t) => ({ id: cryptoRandomId(), name: t.name, body: t.body }));
        if (clean.length === 0) throw new Error("No valid templates in file");
        setTemplates(clean);
        setSelectedId(clean[0].id);
      } catch (err: any) {
        alert((err && err.message) || "Import failed");
      }
    };
    reader.readAsText(file);
  }

  function onImportCsv(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCSV(String(reader.result));
        if (rows.length === 0) throw new Error("Empty CSV");
        // Expect headers: name,body
        const headers = rows[0].map((h) => h.trim().toLowerCase());
        const nameIdx = headers.indexOf("name");
        const bodyIdx = headers.indexOf("body");
        if (nameIdx === -1 || bodyIdx === -1) throw new Error("CSV must have headers: name,body");
        const clean = rows.slice(1)
          .filter((r) => r[bodyIdx] && r[bodyIdx].trim().length > 0)
          .map((r) => ({ id: cryptoRandomId(), name: r[nameIdx] || "Imported", body: r[bodyIdx] }));
        if (clean.length === 0) throw new Error("No valid rows in CSV");
        setTemplates(clean);
        setSelectedId(clean[0].id);
      } catch (err: any) {
        alert((err && err.message) || "CSV import failed");
      }
    };
    reader.readAsText(file);
  }

  function exportJson() {
    const data = templates.map(({ id, ...rest }: Template) => rest);
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wa-templates.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function openWhatsApp() {
    if (!waDigits) {
      alert("Please enter a phone number including country code, e.g. +55 11 96800-0822");
      return;
    }
    addRecent(number);
    const url = primaryUrl || apiUrl; // api fallback is very reliable
    window.open(url, "_blank", "noreferrer");
  }

  // --- Diagnostics (animated checklist) ---
  const [checks, setChecks] = useState<CheckResult[]>([]);
  const [running, setRunning] = useState(false);

  const definitions = useMemo(() => [
    {
      id: "jsx_literals",
      label: "Render literal {{token}} safely in JSX",
      run: () => {
        const sample = "{{" + "name" + "}}";
        return sample === "{{name}}";
      },
    },
    {
      id: "normalize",
      label: "Normalize +55 11 96800-0822 → 5511968000822",
      run: () => normalizeToWhatsAppDigits("+55 11 96800-0822") === "5511968000822",
    },
    {
      id: "normalize00",
      label: "Normalize 0055 (11) 96800 0822 → 5511968000822",
      run: () => normalizeToWhatsAppDigits("0055 (11) 96800 0822") === "5511968000822",
    },
    {
      id: "placeholders",
      label: "Detect unique placeholders (name, from)",
      run: () => {
        const ph = extractPlaceholders("Hello {{name}} from {{from}} and {{from}}!");
        return Array.isArray(ph) && ph.length === 2 && ph.includes("name") && ph.includes("from");
      },
    },
    {
      id: "fill_replace",
      label: "fillTemplate replaces provided vars",
      run: () => fillTemplate("Hi {{name}}", { name: "Ana" }) === "Hi Ana",
    },
    {
      id: "fill_unknown",
      label: "Unknown tokens are preserved",
      run: () => fillTemplate("Hi {{name}} {{missing}}", { name: "Ana" }) === "Hi Ana {{missing}}",
    },
    {
      id: "strip_toggle",
      label: "Strip unresolved tokens when toggle ON",
      run: () => stripUnresolvedTokens("A {{x}} B") === "A B",
    },
    {
      id: "csv_parse",
      label: "CSV parser handles quotes & commas",
      run: () => {
        const rows = parseCSV('name,body\nWelcome,"Hi, {{name}}"');
        return rows.length === 2 && rows[1][0] === 'Welcome' && rows[1][1] === 'Hi, {{name}}';
      },
    },
    {
      id: "domain_force_web",
      label: "Domain toggle: web.whatsapp.com forced",
      run: () => {
        const digits = normalizeToWhatsAppDigits(DEFAULT_NUMBER);
        const url = getUrlForMode('web', digits, 'test');
        return url.startsWith('https://web.whatsapp.com/send?') && url.includes('text=');
      },
    },
    {
      id: "domain_force_api",
      label: "Domain toggle: api.whatsapp.com forced",
      run: () => {
        const digits = normalizeToWhatsAppDigits(DEFAULT_NUMBER);
        const url = getUrlForMode('api', digits, 'test');
        return url.startsWith('https://api.whatsapp.com/send?') && url.includes('phone=') && url.includes('text=');
      },
    },
    {
      id: "domain_force_wame",
      label: "Domain toggle: wa.me forced",
      run: () => {
        const digits = normalizeToWhatsAppDigits(DEFAULT_NUMBER);
        const url = getUrlForMode('wame', digits, 'test');
        return url.startsWith('https://wa.me/') && url.includes('?text=');
      },
    },
  ], []);

  function runDiagnostics() {
    setRunning(true);
    setChecks([]);
    const results: CheckResult[] = [];
    definitions.forEach((def, idx) => {
      setTimeout(() => {
        let ok = false;
        try { ok = !!def.run(); } catch { ok = false; }
        results.push({ id: def.id, label: def.label, ok });
        setChecks([...results]);
        if (idx === definitions.length - 1) setRunning(false);
      }, 300 * (idx + 1));
    });
  }

  useEffect(() => {
    runDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const domainLabel = (m: string) => ({
    auto: "Auto",
    web: "Web",
    api: "API",
    wame: "wa.me",
  }[m] || "Auto");

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-full bg-gradient-to-br from-yellow-300 to-orange-400 shadow-2xl">
            <span className="text-3xl">⚡</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2 drop-shadow-lg">ZapPilot</h1>
          <div className="text-xl text-white/90 font-medium">WhatsApp templates + one-click send 🌅</div>
        </header>

        {/* Number + Quick actions */}
        <section className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-white/20">
            <label className="block text-lg font-bold mb-3 text-gray-800">📞 Recipient Number</label>
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="+55 11 96800-0822"
              className="w-full rounded-2xl border-2 border-orange-200 px-4 py-3 text-lg font-medium focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
            />
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-teal-50 p-3 rounded-xl">
                <span className="font-semibold text-teal-700">📱 WhatsApp Digits:</span>
                <span className="font-mono text-teal-900 bg-white px-2 py-1 rounded">{waDigits || "–"}</span>
              </div>
              <div className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-xl">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500" checked={!!strip} onChange={(e) => setStrip(!!e.target.checked)} />
                  <span className="font-semibold text-emerald-700">✂️ Strip unresolved tokens</span>
                </label>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-xl">
                <label className="font-semibold text-orange-700">🌐 Open with:</label>
                <select
                  className="rounded-xl border-2 border-orange-200 px-3 py-2 font-medium focus:ring-2 focus:ring-orange-300"
                  value={domainMode}
                  onChange={(e) => setDomainMode(e.target.value)}
                >
                  <option value="auto">Auto (Smart)</option>
                  <option value="web">Web App</option>
                  <option value="api">API</option>
                  <option value="wame">wa.me</option>
                </select>
              </div>
              {recents?.length ? (
                <div className="flex flex-wrap gap-2 md:justify-end">
                  <span className="text-gray-600 font-medium">Recent:</span>
                  {recents.map((n: string) => (
                    <button key={n} type="button" onClick={() => setNumber(n)} className="px-3 py-1.5 text-xs font-medium rounded-xl bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 hover:from-yellow-200 hover:to-orange-200 transition-all border border-orange-200">
                      {n}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 flex flex-col gap-4 border border-white/20">
            <button
              onClick={openWhatsApp}
              className="rounded-2xl px-6 py-4 font-bold text-lg bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              title={`Open WhatsApp (${domainLabel(domainMode)}) with the compiled message`}
              type="button"
            >
              🚀 Open WhatsApp ({domainLabel(domainMode)})
            </button>
            <a
              href={apiUrl || undefined}
              target="_blank"
              rel="noreferrer"
              className={classNames(
                "text-center rounded-2xl px-6 py-3 font-bold text-sm transition-all",
                apiUrl ? "bg-gradient-to-r from-blue-500 to-teal-500 text-white hover:from-blue-600 hover:to-teal-600 shadow-lg hover:shadow-xl transform hover:scale-105" : "bg-gray-200 text-gray-400 pointer-events-none"
              )}
            >
              🔗 Copy Link / Fallback (API)
            </a>
            <button onClick={runDiagnostics} type="button" className="rounded-2xl px-6 py-3 font-bold text-sm bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all transform hover:scale-105">🔍 Run diagnostics</button>
            <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded-xl">Primary uses <code className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-mono">{domainLabel(domainMode)}</code>. Fallback uses <code className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-mono">api.whatsapp.com</code>.</p>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Templates list */}
          <aside className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 lg:col-span-1 border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">📋 Templates</h2>
              <div className="flex gap-2 flex-wrap">
                <button onClick={addTemplate} className="text-sm font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white hover:from-green-500 hover:to-emerald-600 shadow-md hover:shadow-lg transition-all" type="button">➕ Add</button>
                <button onClick={exportJson} className="text-sm font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-blue-400 to-teal-500 text-white hover:from-blue-500 hover:to-teal-600 shadow-md hover:shadow-lg transition-all" type="button">📤 Export</button>
                <button onClick={() => fileInputRef.current?.click()} className="text-sm font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:from-yellow-500 hover:to-orange-600 shadow-md hover:shadow-lg transition-all" type="button">📥 JSON</button>
                <button onClick={() => csvInputRef.current?.click()} className="text-sm font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 text-white hover:from-orange-500 hover:to-red-600 shadow-md hover:shadow-lg transition-all" type="button">📊 CSV</button>
                <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={onImportJson} />
                <input ref={csvInputRef} type="file" accept="text/csv,.csv" className="hidden" onChange={onImportCsv} />
              </div>
            </div>

            <ul className="space-y-3 max-h-96 overflow-auto pr-2">
              {templates.map((t: Template) => (
                <li key={t.id} className="group">
                  <button
                    onClick={() => setSelectedId(t.id)}
                    className={classNames(
                      "w-full text-left px-4 py-3 rounded-2xl border-2 transition-all",
                      selected?.id === t.id
                        ? "border-orange-400 bg-gradient-to-r from-orange-50 to-red-50 shadow-lg"
                        : "border-gray-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:border-gray-300"
                    )}
                    title={t.name}
                    type="button"
                  >
                    <div className="font-bold truncate text-gray-800">{t.name}</div>
                    <div className="text-sm text-gray-600 line-clamp-2 mt-1">{t.body}</div>
                  </button>
                  <div className="flex gap-3 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => duplicateTemplate(t.id)} className="text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-lg transition-all" type="button">📋 Duplicate</button>
                    <button onClick={() => deleteTemplate(t.id)} className="text-xs font-semibold text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-lg transition-all" type="button">🗑️ Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          {/* Editor / Variables / Preview */}
          <main className="lg:col-span-2 space-y-6">
            <section className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-white/20">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2">
                  <label className="block text-lg font-bold mb-3 text-gray-800">📝 Template name</label>
                  <input
                    value={selected?.name || ""}
                    onChange={(e) =>
                      setTemplates((xs: Template[]) =>
                        xs.map((t: Template) => (t.id === selected.id ? { ...t, name: e.target.value } : t))
                      )
                    }
                    className="w-full rounded-2xl border-2 border-orange-200 px-4 py-3 font-medium focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
                  />
                </div>
                <div className="lg:col-span-1">
                  <label className="block text-lg font-bold mb-3 text-gray-800">🏷️ Variables</label>
                  <div className="flex flex-wrap gap-2">
                    {placeholderKeys.length === 0 ? (
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-xl">None detected</span>
                    ) : (
                      placeholderKeys.map((k: string) => (
                        <code key={k} className="text-xs font-bold bg-gradient-to-r from-yellow-200 to-orange-300 text-orange-800 rounded-xl px-3 py-2">{"{{" + k + "}}"}</code>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <label className="block text-lg font-bold mt-6 mb-3 text-gray-800">✏️ Template body</label>
              <textarea
                value={selected?.body || ""}
                onChange={(e) =>
                  setTemplates((xs: Template[]) =>
                    xs.map((t: Template) => (t.id === selected.id ? { ...t, body: e.target.value } : t))
                  )
                }
                rows={6}
                className="w-full rounded-2xl border-2 border-orange-200 px-4 py-3 font-mono text-sm focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
              />
            </section>

            <section className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-gray-800">⚙️ Variables</h3>
              {placeholderKeys.length === 0 ? (
                <div className="text-center py-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl">
                  <p className="text-gray-600 mb-2">No variables to fill. Add tokens like <code className="bg-yellow-200 text-orange-800 rounded px-2 py-1 font-bold">{"{{name}}"}</code> to your template.</p>
                  <span className="text-4xl">🤷‍♂️</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {placeholderKeys.map((k: string) => (
                    <div key={k} className="bg-gradient-to-r from-blue-50 to-teal-50 p-4 rounded-2xl border border-blue-200">
                      <label className="block text-sm font-bold mb-2 text-teal-800">🔤 {k}</label>
                      <input
                        value={vars[k] ?? ""}
                        onChange={(e) => setVars((v: Record<string, string>) => ({ ...v, [k]: e.target.value }))}
                        placeholder={`Enter ${k}`}
                        className="w-full rounded-xl border-2 border-teal-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all"
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-800">👁️ Preview</h3>
                <button onClick={() => navigator.clipboard.writeText(compiled)} className="text-sm font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white hover:from-green-500 hover:to-emerald-600 shadow-md hover:shadow-lg transition-all" type="button">📋 Copy text</button>
              </div>
              <div className="rounded-2xl border-2 border-green-200 p-4 bg-gradient-to-r from-green-50 to-emerald-50 whitespace-pre-wrap min-h-[100px] font-medium">{compiled || <span className="text-gray-400 italic">Your message will appear here like magic ✨</span>}</div>
            </section>

            <section className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-800">🔬 Diagnostics</h3>
                <button onClick={runDiagnostics} type="button" className="text-sm font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 text-white hover:from-orange-500 hover:to-red-600 shadow-md hover:shadow-lg transition-all">🔄 Run again</button>
              </div>
              <ul className="space-y-3">
                {definitions.map((d) => {
                  const state = checks.find((c: CheckResult) => c.id === d.id);
                  const isPending = running && !state;
                  const isDone = !!state;
                  const ok = !!state?.ok;
                  return (
                    <li key={d.id} className={classNames("flex items-center gap-4 p-4 rounded-2xl border-2 transition-all", isDone ? (ok ? "border-green-300 bg-gradient-to-r from-green-50 to-emerald-50" : "border-red-300 bg-gradient-to-r from-red-50 to-pink-50") : "border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100") }>
                      <div className="w-6 h-6 flex-shrink-0">
                        {isPending ? <Spinner className="w-6 h-6 text-orange-600" /> : ok ? <CheckIcon className="w-6 h-6 text-green-600" /> : <span className="inline-block w-6 h-6 rounded-full bg-red-500 text-white text-center text-sm font-bold leading-6">✕</span>}
                      </div>
                      <div className={classNames("text-sm font-medium", ok ? "text-green-700" : isDone ? "text-red-700" : "text-gray-700")}>{d.label}</div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-sm text-gray-600 bg-orange-50 p-3 rounded-xl border border-orange-200">⏱️ Animated checks reveal every ~300ms. All processing happens locally - no network required! 🔒</p>
            </section>
          </main>
        </div>

        <footer className="mt-12 text-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
            <p className="text-sm text-gray-700 mb-2">💡 <strong>Pro Tip:</strong> Use <code className="bg-yellow-200 text-orange-800 px-2 py-1 rounded font-bold">{"{{name}}"}</code> or <code className="bg-yellow-200 text-orange-800 px-2 py-1 rounded font-bold">{"{{from}}"}</code> in your template. Variables are auto-detected like magic! ✨</p>
            <p className="text-xs text-gray-500">Made with 🧡 for WhatsApp power users</p>
          </div>
        </footer>
      </div>
    </div>
  );
}